import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
    DropdownWidget,
    IntegerSpecificationModel,
    ModuleInputType,
    ModuleParameter,
    ModuleParamsChangedEvent,
    ModuleSpecification,
    ModuleV1Read,
    ModuleVersion,
    NumberSpecificationModel,
    RadioButtonGroupWidget,
    RangeSpecificationModel,
    RunnerModuleParameter,
    SpecProperty,
    Step,
    StepInput,
    StringResource,
    UIProperty,
    UiControlType,
} from '@ca/core/models';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, startWith } from 'rxjs/operators';
import { CustomValidators } from '../../../../utils/custom-validators/custom-validators';
import { ModuleDetailModalComponent } from '../module-detail-modal/module-detail-modal.component';

@UntilDestroy()
@Component({
    selector: 'ca-module-parameter',
    templateUrl: './module-parameter.component.html',
    styleUrls: ['./module-parameter.component.scss'],
})
export class ModuleParameterComponent implements OnInit, OnChanges {
    @Input() isDisabled: boolean = false;
    @Input() module: ModuleV1Read;
    @Input() input: StepInput;
    @Input() isStepByStepRun: boolean = false;
    @Input() isActiveInStepByStep: boolean = false;
    @Input() manualSliderRefresh: EventEmitter<void>;
    @Input() step?: Step;

    @Output() onParamsChanged: EventEmitter<ModuleParamsChangedEvent> = new EventEmitter<ModuleParamsChangedEvent>();
    @Output() onClickRerunButton: EventEmitter<Object> = new EventEmitter<Object>();
    @Output() onAddDefineInput: EventEmitter<{ paramInputType: ModuleInputType; parameterKey: string }> = new EventEmitter<{
        paramInputType: ModuleInputType;
        parameterKey: string;
    }>();

    formData: UntypedFormGroup;
    parameters: Array<RunnerModuleParameter>;

    // Add all possible error messages for module parameters here
    private readonly errorMessages: Map<string, string> = new Map([
        ['required', 'MODULE_SPEC.FORM_ERRORS.REQUIRED'],
        ['max', 'MODULE_SPEC.FORM_ERRORS.DEFINE_MAX_VALUE'],
        ['min', 'MODULE_SPEC.FORM_ERRORS.DEFINE_MIN_VALUE'],
        ['invalidDefaultValue', 'MODULE_SPEC.FORM_ERRORS.INVALID_DEFAULT_VALUE'],
    ]);

    get haveEditableParameters(): boolean {
        return this.parameters.length > 0;
    }
    constructor(
        private modalService: NgbModal,
        private translateService: TranslateService,
    ) {}

    ngOnInit(): void {
        this.parameters = this.convertSpecToFormParams(this.module.selectedModuleVersion.specification);
        this.parameters = this.overrideDefaultValuesWithUserInput(this.parameters, this.input);
        this.generateAndSetFormOutOfParams(this.parameters);
    }

    ngOnChanges(changes: SimpleChanges): void {
        // #377161: It's not enough to just add the property [disabled] to each control, since it's complaining that we should use
        // formData.disable() for reactive forms.
        if (changes.isDisabled && this.formData) {
            if (changes.isDisabled.currentValue === true) {
                this.formData.disable();
            } else if (changes.isDisabled.currentValue === false) {
                this.formData.enable();
            }
        }
    }

    resetParameters(): void {
        this.parameters = this.overrideDefaultValuesWithUserInput(this.parameters, this.input, true);
        this.generateAndSetFormOutOfParams(this.parameters);
    }

    openModuleDetails(): void {
        const modalRef: NgbModalRef = this.modalService.open(ModuleDetailModalComponent, { centered: true });
        const moduleDetailModal = <ModuleDetailModalComponent>modalRef.componentInstance;
        moduleDetailModal.moduleId = this.module.id;
    }

    openDefineInputDialog(paramInputType: ModuleInputType, parameterKey: string): void {
        this.onAddDefineInput.emit({ paramInputType, parameterKey });
    }

    reRunButtonClick(): void {
        const values = this.preprocessValues(this.formData.value);
        this.onClickRerunButton.emit(values);
    }

    shouldShowParamLabel(param: RunnerModuleParameter): boolean {
        let showLabel = false;
        if (this.step != null) {
            showLabel = param.widget !== 'none' && !this.step.hasParameterConnection(param.key);
        }

        return showLabel;
    }

    getErrorKeysFromModuleParameter(param: RunnerModuleParameter): string[] {
        return param.control && param.control.errors ? Object.keys(param.control.errors) : [];
    }

    getErrorMessage(key: string, param?: RunnerModuleParameter): string {
        const messageKey = this.errorMessages.get(key);

        let translatedMessage = '';

        if (messageKey != null) {
            // 389386: Add number range to error message for min and max validation
            if (param != null && (key === 'min' || key === 'max')) {
                const numberRangeString = param.min + ' and ' + param.max;
                translatedMessage = this.translateService.instant(messageKey, { numberRangeString });
            } else {
                translatedMessage = this.translateService.instant(messageKey);
            }

            return translatedMessage;
        } else {
            return 'No translation found for ' + key;
        }
    }

    getParamTooltip(param: RunnerModuleParameter): string | undefined {
        let tooltip: string | undefined = undefined;
        if (this.step != null) {
            const moduleParam: ModuleParameter | undefined = this.step.moduleVersion.getInputParameter(param.key);
            if (moduleParam != null) {
                tooltip = moduleParam.description;
            }
        }
        return tooltip;
    }

    isInputTypeNumber(type: ModuleInputType): boolean {
        let isNumber: boolean = false;
        if (type === ModuleInputType.Number || type === ModuleInputType.Integer) {
            isNumber = true;
        }

        return isNumber;
    }

    private handleFormValueChange(values: Object): void {
        if (values) {
            values = this.preprocessValues(values);
            this.onParamsChanged.emit({ params: values as Object, form: this.formData });
        }
    }

    private preprocessValues(values: Object): Object {
        values = this.replaceNumberStringsWithNumbers(values);
        values = this.processRangeSliderValues(values);

        return values;
    }

    /**
     * Range Slider values come in a format [minValue, maxValue].
     * However, we need to save them in the format:
     * {
     *      lower_inclusive: minValue,
     *      upper_inclusive: maxValue
     * }
     * @param values the values
     */
    private processRangeSliderValues(values: Object): Object {
        this.parameters
            .filter((param: RunnerModuleParameter) => param.widget === 'slider_range')
            .map((param: RunnerModuleParameter) => {
                if (values != null && values[param.key] != null && values[param.key].length > 0) {
                    const minValue: number = values[param.key][0];
                    const maxValue: number = values[param.key][1];

                    values[param.key] = {
                        lower_inclusive: minValue,
                        upper_inclusive: maxValue,
                    };
                }
            });
        return values;
    }

    private setSubscriptionOnFormValueChanges(): void {
        if (this.formData) {
            this.formData.valueChanges
                .pipe(debounceTime(500), startWith(this.formData.value), untilDestroyed(this))
                .subscribe(value => this.handleFormValueChange(value));
        }
    }

    /**
     * Sets the values from user input to the form.
     * When shouldResetToDefault is true, resets the values to the default values.
     */
    private overrideDefaultValuesWithUserInput(
        parameters: RunnerModuleParameter[],
        input?: StepInput,
        shouldResetToDefault: boolean = false,
    ): RunnerModuleParameter[] {
        if (input != null) {
            parameters.forEach((param: RunnerModuleParameter) => {
                if (input[param.key] != null) {
                    const newVal = input[param.key];
                    param.inputValue = newVal;
                }

                // Reset parameters to their default (=value from module spec). However, do not reset connections.
                if (shouldResetToDefault === true && param.inputValue != null && param.inputValue.operation_id == null) {
                    param.inputValue = param.defaultValue;
                }
            });
        }

        return parameters;
    }

    private convertSpecToFormParams(moduleSpec: ModuleSpecification): Array<RunnerModuleParameter> {
        const params: Array<RunnerModuleParameter> = [];

        const inputParamKeys: Array<string> = this.getInputKeysToShowInUi(moduleSpec);

        inputParamKeys.map((key: string) => {
            const inputValueFromSpec: SpecProperty<string> = moduleSpec.spec.inputs[key];

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const type: string | undefined = ModuleVersion.getParameterTypeFromSpec(inputValueFromSpec as SpecProperty<any>);
            if (type == null) {
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                throw new Error('Cannot get parameter type from spec: ' + inputValueFromSpec);
            }
            const inputType = ModuleVersion.mapTypeToModuleInputType(type);
            const specUiPart: UIProperty | undefined = moduleSpec.ui && moduleSpec.ui.inputs[key];
            const widget: UiControlType = this.getParamWidgetType(inputType, specUiPart);

            const defaultValue = inputValueFromSpec.default;
            const inputValue = defaultValue;

            let label: string | undefined;

            // Default the index to 99, because we want to have Files and ListFiles at last. All other cases
            // should have a UI part in the module spec, and thus get the index from that part
            let index: number = 99;

            if (specUiPart != null) {
                index = specUiPart.index;
                label = specUiPart.label;
            }

            // If there's no label (i.e. because there's no UI part), take the key as label
            if (label == null) {
                label = key;
            }

            const uiParam: RunnerModuleParameter = {
                key,
                label,
                widget,
                index,
                inputType,
                defaultValue,
                inputValue,
                validators: [Validators.required],
            };

            this.applyWidgetSpecificProps(uiParam, specUiPart, inputValueFromSpec);

            params.push(uiParam);
        });

        // Sort parameters according to their index
        params.sort((a: RunnerModuleParameter, b: RunnerModuleParameter) => a.index - b.index);
        return params;
    }

    /**
     * For specific widget types we need to apply some items
     *
     */
    private applyWidgetSpecificProps(param: RunnerModuleParameter, specUiPart: UIProperty | undefined, inputValueFromSpec: SpecProperty<string>): void {
        if (specUiPart != null) {
            if (specUiPart['widget:radiobuttongroup'] != null) {
                const radioUI: RadioButtonGroupWidget = specUiPart['widget:radiobuttongroup'];
                param.items = radioUI.items;

                if (param.validators.find((v: ValidatorFn) => v.name === 'required') !== undefined) {
                    param.validators.push(CustomValidators.isDefaultValidValidator(param.items as StringResource[]));
                }
            } else if (specUiPart['widget:dropdown'] != null) {
                const items: StringResource[] = (specUiPart['widget:dropdown'] as DropdownWidget).items;
                if (Array.isArray(items)) {
                    param.items = items.map((item: StringResource) => {
                        // This could come in a format of an object (with key and label) or
                        // as string[]
                        if (item.key != null && item.label != null) {
                            return item;
                        } else {
                            return {
                                key: item,
                                label: item,
                            };
                        }
                    });
                    if (param.validators.find((v: ValidatorFn) => v.name === 'required') !== undefined) {
                        param.validators.push(CustomValidators.isDefaultValidValidator(param.items as StringResource[]));
                    }
                }
            } else if (specUiPart['widget:slider_range'] != null) {
                const sliderUIPart = specUiPart['widget:slider_range'];
                const sliderSpecPart = <RangeSpecificationModel>inputValueFromSpec['type:range'];
                const specLower = sliderSpecPart.lower;
                const specUpper = sliderSpecPart.upper;

                param.step = sliderUIPart.step;

                if (specLower != null && specLower.lower_exclusive != null) {
                    param.min = specLower.lower_exclusive + (param.step || 1);
                } else if (specLower != null && specLower.lower_inclusive != null) {
                    param.min = specLower.lower_inclusive;
                }

                if (specUpper != null && specUpper.upper_exclusive != null) {
                    param.max = specUpper.upper_exclusive - (param.step || 1);
                } else if (specUpper != null && specUpper.upper_inclusive != null) {
                    param.max = specUpper.upper_inclusive;
                }

                // A range slider always has to have a default
                // else we get some weird behaviour in the slider
                if (param.defaultValue == null) {
                    // cast as any, because it's read-only
                    (param.defaultValue as Object) = {
                        lower_inclusive: param.min || 1,
                        upper_inclusive: param.max || 1000,
                    };
                }

                // If input value is not set, take the default
                if (param.inputValue == null) {
                    param.inputValue = param.defaultValue;
                }
            } else if (specUiPart['widget:slider'] != null) {
                const sliderUI = specUiPart['widget:slider'];
                param.step = sliderUI.step;
            }

            // 389386: Number types: Add min and max to numbers and integers. This applies for sliders and textboxes
            if (inputValueFromSpec['type:number'] || inputValueFromSpec['type:integer']) {
                const numberSpecPart = <NumberSpecificationModel>inputValueFromSpec['type:number'];
                const integerSpecPart = <IntegerSpecificationModel>inputValueFromSpec['type:integer'];

                if (integerSpecPart != null) {
                    param.min = integerSpecPart.min;
                    param.max = integerSpecPart.max;
                } else if (numberSpecPart != null) {
                    if (numberSpecPart.lower_inclusive != null) {
                        param.min = numberSpecPart.lower_inclusive;
                    }

                    if (numberSpecPart.upper_inclusive != null) {
                        param.max = numberSpecPart.upper_inclusive;
                    }
                }
            }

            // Add min and max validators if number is present
            if (param.min != null) {
                param.validators.push(Validators.min(param.min));
            }
            if (param.max != null) {
                param.validators.push(Validators.max(param.max));
            }
        }
    }

    private generateAndSetFormOutOfParams(parameters: RunnerModuleParameter[]): void {
        const group = {};
        parameters.map((param: RunnerModuleParameter) => {
            if (param.widget === 'slider_range') {
                const value: number[] = [];
                if (param.inputValue != null && param.inputValue.lower_inclusive != null) {
                    value.push(param.inputValue.lower_inclusive);
                }

                if (param.inputValue != null && param.inputValue.upper_inclusive != null) {
                    value.push(param.inputValue.upper_inclusive);
                }

                group[param.key] = new UntypedFormControl(value);
            } else if (param.widget !== 'none') {
                group[param.key] = new UntypedFormControl(param.inputValue, Validators.compose(param.validators));
            }

            param.control = group[param.key];
        });

        // Set the newly generated form to scope
        this.formData = new UntypedFormGroup(group);

        // After setting the form, we need to subscribe to it again (else it will not
        // track value changes)
        this.setSubscriptionOnFormValueChanges();
    }

    private getParamWidgetType(type: ModuleInputType, ui?: UIProperty): UiControlType {
        if (type === ModuleInputType.ListFile || type === ModuleInputType.File) {
            return 'input_file_select';
        } else if (ui != null) {
            if (ui['widget:textbox']) {
                return 'textbox';
            } else if (ui['widget:file_dialog']) {
                return 'file_dialog';
            } else if (ui['widget:checkbox']) {
                return 'checkbox';
            } else if (ui['widget:radiobuttongroup']) {
                return 'radiobuttongroup';
            } else if (ui['widget:dropdown']) {
                return 'dropdown';
            } else if (ui['widget:slider_range']) {
                return 'slider_range';
            } else if (ui['widget:slider']) {
                return 'slider';
            } else {
                console.error('Cannot parse widget type. Is it set?', ui);
            }
        }

        return 'none';
    }

    /**
     * Get input parameter keys
     * - without connections
     * - without special params for SnapConnectors
     */
    private getInputKeysToShowInUi(moduleSpec: ModuleSpecification): string[] {
        return Object.keys(moduleSpec.spec.inputs)
            .filter((key: string) => {
                const inputKeyExistsInSpec: boolean = moduleSpec.spec.inputs[key] != null;
                const paramHasConnection: boolean = this.step ? this.step.hasParameterConnection(key) : false;

                return inputKeyExistsInSpec && !paramHasConnection;
            })
            .filter((key: string) => key !== 'var_env' && key !== 'var_token');
    }

    private replaceNumberStringsWithNumbers(values: Object): Object {
        for (const [key, value] of Object.entries(values)) {
            const parameter: RunnerModuleParameter | undefined = this.parameters.find(param => param.key === key);
            if (parameter == null) {
                continue;
            }

            if (typeof value === 'string' && (parameter.inputType === ModuleInputType.Integer || parameter.inputType === ModuleInputType.Number)) {
                let parsedNumber: number = parseInt(value, 10);

                if (parameter.inputType === ModuleInputType.Number) {
                    parsedNumber = parseFloat(value);
                }

                values[key] = isNaN(parsedNumber) ? undefined : parsedNumber;
            }
        }

        return values;
    }
}
